import { TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getSsoConfig, setSsoConfig } from 'services/apis'
import { Button, Input2, MultiSelect, Toggle } from 'stories/components'
import { confirm } from 'utils'

interface ProviderForm {
  id: string
  enabled: boolean
  label: string
  vendor: string
  issuer: string
  clientId: string
  /** Only ever holds a newly typed secret. Blank means "leave the stored one alone". */
  clientSecret: string
  clientSecretSet: boolean
  scopes: string
  /**
   * Raw text exactly as typed, NOT a parsed list.
   *
   * It used to be string[], rendered with join(', ') and re-parsed on every keystroke. That round
   * trip silently ate the comma: typing "a.com," split to ["a.com", ""], filter(Boolean) dropped
   * the empty tail, and join rebuilt "a.com" without the comma just typed — so the field looked
   * like it only accepted letters and digits. Normalising a controlled input on each keystroke
   * only works when the normalisation is lossless, and this one is not. The server splits and
   * normalises the string anyway (`domains()` in sso.config.service.ts takes either shape).
   */
  allowedEmailDomains: string
  redirectUri: string
}

const blankProvider = (): ProviderForm => ({
  id: '',
  enabled: false,
  label: '',
  vendor: 'microsoft',
  issuer: '',
  clientId: '',
  clientSecret: '',
  clientSecretSet: false,
  scopes: 'openid email profile',
  allowedEmailDomains: '',
  redirectUri: '',
})

/** The two providers customers actually ask for, plus an escape hatch for anything else. */
const VENDOR_PRESETS: Record<string, { label: string; issuerHint: string }> = {
  microsoft: {
    label: 'Sign in with Microsoft',
    issuerHint: 'https://login.microsoftonline.com/<directory-id>/v2.0',
  },
  okta: {
    label: 'Sign in with Okta',
    issuerHint: 'https://<your-org>.okta.com',
  },
  other: {
    label: 'Single sign-on',
    issuerHint: 'https://id.example.com',
  },
}

/**
 * Admin Tools > Single Sign-On.
 *
 * Configures the OIDC providers staff may sign in with. Client secrets are write-only: the server
 * never sends one back, so an existing provider shows "a secret is stored" and leaving the field
 * empty keeps it.
 */
export const SingleSignOn = () => {
  const [loading, setLoading] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(true)
  // Account type keys, not labels: a company renames its own account types, and the server matches
  // on the key. Empty is a real answer here - it means everybody signs in through the provider.
  const [passwordLoginAccountTypes, setPasswordLoginAccountTypes] = useState<string[]>([])
  const [providers, setProviders] = useState<ProviderForm[]>([])
  const [redirectBase, setRedirectBase] = useState('')

  const apply = (data: any) => {
    setEnabled(!!data?.enabled)
    setAllowPasswordLogin(data?.allowPasswordLogin !== false)
    setPasswordLoginAccountTypes(Array.isArray(data?.passwordLoginAccountTypes) ? data.passwordLoginAccountTypes : [])
    setProviders(
      (data?.providers || []).map((p: any) => ({
        ...blankProvider(),
        ...p,
        clientSecret: '',
        allowedEmailDomains: Array.isArray(p?.allowedEmailDomains)
          ? p.allowedEmailDomains.join(', ')
          : String(p?.allowedEmailDomains || ''),
      })),
    )
    setRedirectBase(data?.defaultRedirectUriBase || '')
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      apply(await getSsoConfig())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const update = (index: number, key: keyof ProviderForm, value: any) => {
    setProviders((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)))
  }

  const onAdd = () => setProviders((prev) => [...prev, blankProvider()])

  const onRemove = async (index: number) => {
    const provider = providers[index]
    const ok = await confirm(
      `Remove ${provider.label || provider.id || 'this provider'}? Anyone who signs in with it will lose that option.`,
    )
    if (!ok) return
    setProviders((prev) => prev.filter((_, i) => i !== index))
  }

  const onSave = async () => {
    for (const provider of providers) {
      if (!provider.enabled) continue
      if (!/^[a-z0-9-]+$/.test(provider.id)) {
        toast('Each enabled provider needs an ID using lower-case letters, numbers or hyphens.', { type: 'error' })
        return
      }
      if (!provider.issuer || !provider.clientId) {
        toast(`"${provider.label || provider.id}" needs an issuer URL and a client ID.`, { type: 'error' })
        return
      }
      if (!provider.clientSecretSet && !provider.clientSecret) {
        toast(`"${provider.label || provider.id}" needs a client secret.`, { type: 'error' })
        return
      }
    }
    if (new Set(providers.map((p) => p.id)).size !== providers.length) {
      toast('Two providers share the same ID.', { type: 'error' })
      return
    }

    setLoading(true)
    try {
      const saved = await setSsoConfig({
        enabled,
        allowPasswordLogin,
        passwordLoginAccountTypes,
        // `clientSecretSet` is display-only; the server decides what is stored.
        providers: providers.map((p) => {
          const payload: Partial<ProviderForm> = { ...p }
          delete payload.clientSecretSet
          return payload
        }),
      })
      apply(saved)
      toast('Single sign-on settings saved.', { type: 'success' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <h2 className="text-2xl font-bold text-stone-900">Single Sign-On</h2>
      <p className="mt-1 mb-6 max-w-3xl text-[15px] text-stone-500">
        Let staff sign in through your identity provider. Microsoft Entra ID and Okta are both supported, as is any
        other provider that speaks OpenID Connect. Accounts are matched on email address and must already exist here -
        signing in this way never creates an account or grants a role.
      </p>

      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-stone-200 bg-white p-5">
        <Toggle id="sso-enabled" title="Enable single sign-on" value={enabled} onChange={setEnabled} />
        <Toggle
          id="sso-allow-password"
          title="Also allow email and password sign-in"
          value={allowPasswordLogin}
          onChange={setAllowPasswordLogin}
        />
        <p className="text-[13px] text-stone-500">
          Turning password sign-in off makes SSO the only way in. It is ignored unless at least one provider below is
          enabled, so a half-finished setup cannot lock everyone out.
        </p>

        {!allowPasswordLogin && (
          <div className="mt-2 border-t border-stone-200 pt-4">
            <MultiSelect
              id="sso-password-login-account-types"
              title="Account types that keep email and password sign-in"
              options={accountTypes}
              value={passwordLoginAccountTypes}
              onChange={(value: any) => setPasswordLoginAccountTypes(Array.isArray(value) ? value : [])}
            />
            <p className="mt-2 text-[13px] text-stone-500">
              Your identity provider holds your own people. Brokers and other outside users have no account in it, so
              they keep signing in with an email address and password - they are selected here by default. Clear the
              list to require single sign-on for everybody.
            </p>
          </div>
        )}
      </div>

      {providers.map((provider, index) => {
        const preset = VENDOR_PRESETS[provider.vendor] || VENDOR_PRESETS.other
        const callbackUrl = provider.redirectUri || (redirectBase && provider.id ? `${redirectBase}/auth/sso/${provider.id}/callback` : '')
        return (
          <div key={index} className="mb-4 rounded-lg border border-stone-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <Toggle
                id={`sso-provider-enabled-${index}`}
                title="Enabled"
                value={provider.enabled}
                onChange={(v: boolean) => update(index, 'enabled', v)}
              />
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-stone-400 transition hover:text-rose-600"
                aria-label="Remove provider"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-[13px] font-medium text-stone-600">Provider</p>
                <select
                  value={provider.vendor}
                  onChange={(e) => {
                    const vendor = e.target.value
                    update(index, 'vendor', vendor)
                    if (!provider.label) update(index, 'label', VENDOR_PRESETS[vendor]?.label || '')
                  }}
                  className="w-full rounded border border-stone-300 px-3 py-2 text-[15px]"
                >
                  <option value="microsoft">Microsoft Entra ID</option>
                  <option value="okta">Okta</option>
                  <option value="other">Other (OpenID Connect)</option>
                </select>
              </div>

              <Input2
                title="ID (used in the sign-in URL, cannot change later)"
                value={provider.id}
                onChange={(v: string) => update(index, 'id', v.trim().toLowerCase())}
              />
              <Input2
                title="Button label"
                value={provider.label}
                placeholder={preset.label}
                onChange={(v: string) => update(index, 'label', v)}
              />
              <Input2
                title="Issuer URL"
                value={provider.issuer}
                placeholder={preset.issuerHint}
                onChange={(v: string) => update(index, 'issuer', v)}
              />
              <Input2
                title="Client ID"
                value={provider.clientId}
                onChange={(v: string) => update(index, 'clientId', v)}
              />
              <Input2
                type="password"
                title={provider.clientSecretSet ? 'Client secret (stored - type to replace)' : 'Client secret'}
                value={provider.clientSecret}
                onChange={(v: string) => update(index, 'clientSecret', v)}
              />
              <Input2
                title="Scopes"
                value={provider.scopes}
                onChange={(v: string) => update(index, 'scopes', v)}
              />
              <Input2
                title="Restrict to email domains (comma separated, optional)"
                value={provider.allowedEmailDomains}
                onChange={(v: string) => update(index, 'allowedEmailDomains', v)}
              />
              <Input2
                title="Redirect URI override (optional)"
                value={provider.redirectUri}
                onChange={(v: string) => update(index, 'redirectUri', v)}
              />
            </div>

            {callbackUrl && (
              <p className="mt-4 break-all rounded bg-stone-50 px-3 py-2 text-[13px] text-stone-600">
                Register this exact redirect URI with the provider: <span className="font-mono">{callbackUrl}</span>
              </p>
            )}
          </div>
        )
      })}

      <div className="mt-6 flex items-center gap-3">
        <Button color="gray" onClick={onAdd}>
          Add provider
        </Button>
        <Button color="blue" onClick={onSave} loading={loading}>
          Save
        </Button>
      </div>
    </div>
  )
}
